/**
 * SINGLE SOURCE OF TRUTH for AutoCAD's built-in arrowhead NAMES (Codex SL-11
 * round-3 #2).
 *
 * {@link BUILTIN_ACAD_ARROW_NAMES} is a verbatim mirror of ezdxf 1.4.3
 * `render/arrows.py` `_Arrows.__acad__` MINUS the empty closed-filled entry
 * (`""`, which resolves to the null handle `0`, not a block). These arrows live
 * in AutoCAD's code, NOT the BLOCKS table.
 *
 * Both the export handle-resolution stamp (`dxfBridge.dimStyleArrowhead.ts`) and
 * the reference-audit arrowhead sentinel whitelist (`referenceAudit.sentinels.ts`
 * → also consumed by `dependencyGraph.ts`) derive from this one set, so a strict
 * pre-export audit can NEVER flag as UNDEFINED_BLOCK a `dimblk` override the
 * exporter resolves (and vice-versa). Before this module, the exporter accepted
 * the unprefixed `OPEN90`/`DOT` spellings while the audit whitelist only carried
 * the underscored `_open90` — so `dimblk:"OPEN90"` false-flagged.
 *
 * NOT the ezdxf-special `EZ_ARROW*` set (`__ezdxf__`): those are not AutoCAD
 * arrows and, absent a real block, are treated as custom → dropped.
 *
 * Source of truth: ezdxf 1.4.3 `render/arrows.py` `_Arrows.__acad__` +
 * `block_name()` (prepends `_` to an acad arrow name). AutoCAD MCP is BANNED.
 */
export const BUILTIN_ACAD_ARROW_NAMES: ReadonlySet<string> = new Set<string>([
  "ARCHTICK",
  "BOXBLANK",
  "BOXFILLED",
  "CLOSED",
  "CLOSEDBLANK",
  "DATUMBLANK",
  "DATUMFILLED",
  "DOT",
  "DOTBLANK",
  "DOTSMALL",
  "INTEGRAL",
  "NONE",
  "OBLIQUE",
  "OPEN",
  "OPEN30",
  "OPEN90",
  "ORIGIN",
  "ORIGIN2",
  "SMALL",
]);

/**
 * The ezdxf null handle for the default CLOSEDFILLED arrowhead (`set_arrow_handle`
 * maps `_CLOSEDFILLED` → `"0"`). Shared by the export stamp, the audit-sentinel
 * set, and {@link resolveArrowheadRef} so every site denotes the default the same
 * way (SL-11 round-5 R14).
 */
export const DEFAULT_ARROWHEAD_HANDLE = "0";

/**
 * The arrowhead-name SENTINEL set for the reference audit (stored LOWERCASE) —
 * every value the export stamp resolves WITHOUT a user-declared BLOCK_RECORD, so
 * the audit and the exporter AGREE (a `dimblk` / arrowheadBlock override naming
 * one of these is NOT an UNDEFINED_BLOCK). Derived from
 * {@link BUILTIN_ACAD_ARROW_NAMES} so the two sites cannot drift.
 *
 * Covers BOTH spellings the model can carry for each built-in arrow:
 *   - the unprefixed AutoCAD / ezdxf arrow name (`open90`, `dot`), and
 *   - the canonical `_<NAME>` BLOCK_RECORD name (`_open90`, `_dot`) that ezdxf
 *     `block_name()` prepends `_` to,
 * plus the three closed-filled default spellings the stamp maps to the null
 * handle `0`: `.` (AutoCAD DIMBLK sentinel), `_closedfilled` (the block name),
 * and `0` itself — the null-handle default an AUTHORED `dimblk:"0"` denotes
 * (SL-11 round-5 R14: the export stamp maps `"0"` → `1005 0` unconditionally, so
 * the audit + dependency must treat it as the default arrow, NOT a real block
 * literally named `0`). The empty string `""` is "unset" — handled by
 * `checkName`'s required-flag branch, NEVER by a sentinel — so it is absent here.
 */
export const ARROWHEAD_NAME_SENTINELS: ReadonlySet<string> = new Set<string>([
  ".",
  "_closedfilled",
  DEFAULT_ARROWHEAD_HANDLE,
  ...[...BUILTIN_ACAD_ARROW_NAMES].flatMap((name) => {
    const lower = name.toLowerCase();
    return [lower, `_${lower}`];
  }),
]);

/**
 * The canonical `_<NAME>` BLOCK_RECORD name for a built-in AutoCAD arrow override
 * value, or `null` when the value is not a built-in arrow. Mirrors ezdxf
 * `ARROWS.block_name` for the `__acad__` set: strip a single leading `_`, upper-
 * case, verify membership, then re-prefix with `_` (AutoCAD arrow blocks are the
 * reserved `_<NAME>` names ezdxf's readback strips back to `NAME`). The empty /
 * closed-filled cases are handled by the default branch, not here.
 *
 * MOVED here (SL-11 round-5) from `dxfBridge.dimStyleArrowhead.ts` so the export
 * stamp, the audit, and the dependency index all derive the canonical arrow-block
 * name from ONE place — a built-in arrow's `_<NAME>` cannot drift between the site
 * that SYNTHESIZES/ADOPTS it (export) and the sites that must AGREE it is not an
 * undefined block / is a live dependent when the canonical block is declared.
 */
export function builtinArrowBlockRecordName(value: string): string | null {
  const bare = (value.startsWith("_") ? value.slice(1) : value).toUpperCase();
  if (!BUILTIN_ACAD_ARROW_NAMES.has(bare)) return null;
  return `_${bare}`;
}

/**
 * The single, discriminated classification of a DIMENSION / MLEADER arrowhead-
 * field override value (`dimblk*` / `dimblk*Handle` / `arrowheadBlock`). This is
 * the ONE resolution the reference audit and the dependency index consult so they
 * cannot diverge on "what block/handle does this value reference", and it MIRRORS
 * the export stamp's own resolution ordering (`dxfBridge.dimStyleArrowhead.ts`
 * `resolveArrowheadValue`) exactly — so all three sites agree by construction:
 *
 *   - `default`             — closed-filled / null-handle default (`"" / "." /
 *     "_CLOSEDFILLED" / "0"`). Export emits `1005 0`; no dependency; no undefined-
 *     block error.
 *   - `block-name`          — a REAL, currently-DECLARED block. INCLUDES the
 *     canonicalized built-in: an unprefixed `DOT` whose canonical `_DOT` block IS
 *     declared resolves to `_DOT` (R12), because the export ADOPTS that record. A
 *     live dependent (PURGE must keep the block).
 *   - `block-handle`        — an IMPORTED soft-pointer HANDLE (`1005`) that targets
 *     a live BLOCK_RECORD; carries the resolved block `name` so the dependency
 *     index records a dependent on THAT block (R13 — PURGE keeps a block referenced
 *     ONLY by handle).
 *   - `builtin-synthesized` — a built-in arrow whose canonical `_<NAME>` block is
 *     NOT declared; the export SYNTHESIZES it on demand. Not a purgeable current-
 *     document dependency and not an undefined-block error.
 *   - `unresolved`          — a custom name with no block / a dangling imported
 *     handle; the export DROPS the override. No dependency (the audit surfaces an
 *     UNDEFINED_BLOCK for the authored-name case via its own sentinel projection).
 */
export type ArrowheadRefResolution =
  | { readonly kind: "default" }
  | { readonly kind: "block-name"; readonly name: string }
  | { readonly kind: "block-handle"; readonly handle: string; readonly name: string }
  | { readonly kind: "builtin-synthesized"; readonly name: string }
  | { readonly kind: "unresolved" };

/**
 * The PROVENANCE of an arrowhead value — an AUTHORED block NAME (`dimblk*` /
 * `arrowheadBlock`, DSTYLE `1000` placeholder) vs an IMPORTED soft-pointer HANDLE
 * (`dimblk*Handle`, DSTYLE `1005`). The two are disjoint resolution paths (an
 * imported handle is NEVER re-read as a same-spelled block NAME, and vice-versa —
 * the `A1`-handle vs `A1`-named-block ambiguity, SL-11 round-3 #3).
 */
export type ArrowheadRefProvenance = "name" | "handle";

/**
 * Context for {@link resolveArrowheadRef}. Both maps are keyed LOWERCASE
 * (case-insensitive DXF names + hex handles). Omit a map at a site with no such
 * context (a `name`-provenance resolve needs only `definedBlockNames`; a
 * `handle`-provenance resolve needs only `blockRecordHandlesToName`).
 */
export interface ArrowheadResolveContext {
  /** LOWERCASED block NAMES the document declares (`Object.keys(document.blocks)`). */
  readonly definedBlockNames?: ReadonlySet<string>;
  /** LOWERCASED BLOCK_RECORD handle → its block NAME (imported-handle resolution). */
  readonly blockRecordHandlesToName?: ReadonlyMap<string, string>;
}

const DEFAULT_RESOLUTION: ArrowheadRefResolution = { kind: "default" };
const UNRESOLVED_RESOLUTION: ArrowheadRefResolution = { kind: "unresolved" };

/**
 * The closed-filled / null-handle default arrowhead predicate — the ONE truth for
 * "this override value denotes the default CLOSEDFILLED arrow, not a block". Covers
 * every spelling the export stamp maps to the null handle `0`: the empty string
 * (unset), AutoCAD's DIMBLK "." sentinel, the `_CLOSEDFILLED` block name, and the
 * literal null handle `"0"` (an authored `dimblk:"0"` / an imported `1005 0` — R14).
 * Shared by BOTH the DIMENSION resolver ({@link resolveArrowheadRef}) and the MLEADER
 * resolver ({@link resolveMleaderArrowheadRef}) + the reference audit so the default
 * cannot drift between the sites that classify an arrowhead value.
 */
export function isDefaultArrowheadValue(value: string): boolean {
  const key = value.toLowerCase();
  return (
    value === "" ||
    key === "." ||
    key === "_closedfilled" ||
    value === DEFAULT_ARROWHEAD_HANDLE
  );
}

/**
 * Classify one arrowhead-field override value. Ordering MIRRORS the export stamp's
 * `resolveArrowheadValue` (default → imported-handle-passthrough → exact-name →
 * canonical-built-in → drop) so the audit / dependency verdicts can never diverge
 * from what the exporter actually emits (see {@link ArrowheadRefResolution}).
 *
 * DIMENSION DSTYLE ONLY — a `dimblk*` NAME / `dimblk*Handle` HANDLE, which
 * `stampDimensionDStyleArrowheadHandles` canonicalizes an unprefixed built-in into
 * its `_<NAME>` record (R12 adopt) and SYNTHESIZES an absent built-in on export. The
 * MLEADER arrowhead paths are exported VERBATIM (no synthesis) and MUST use
 * {@link resolveMleaderArrowheadRef} instead (SL-11 round-6 @codex — applying this
 * DIMENSION semantics to MLEADER recorded a false `_DOT` dependency / hid a dangling
 * MLEADER arrow).
 */
export function resolveArrowheadRef(
  value: string,
  provenance: ArrowheadRefProvenance,
  ctx: ArrowheadResolveContext,
): ArrowheadRefResolution {
  const key = value.toLowerCase();
  // (1) Closed-filled / null-handle default — BOTH provenances, FIRST (mirrors the
  // export stamp's leading `"" / "." / "_CLOSEDFILLED" / "0"` branch, which wins
  // even over a same-spelled block, so an authored `dimblk:"0"` is the default
  // arrow, never a phantom block named `0` — R14).
  if (isDefaultArrowheadValue(value)) {
    return DEFAULT_RESOLUTION;
  }
  // (2) IMPORTED soft-pointer HANDLE (`1005`): kept iff it targets a live
  // BLOCK_RECORD; never re-read as a same-spelled block NAME (#3). Carries the
  // resolved block name so the dependency index records a dependent on THAT block
  // (R13). A dangling imported handle → dropped on export.
  if (provenance === "handle") {
    const name = ctx.blockRecordHandlesToName?.get(key);
    if (name !== undefined) return { kind: "block-handle", handle: value, name };
    return UNRESOLVED_RESOLUTION;
  }
  // (3) AUTHORED NAME — an EXACT declared block wins (export F8 name-first).
  if (ctx.definedBlockNames?.has(key)) return { kind: "block-name", name: value };
  // (4) A built-in AutoCAD arrow → its canonical `_<NAME>` BLOCK_RECORD. When the
  // doc DECLARES that canonical block (stores `DOT`, declares `_DOT`) the export
  // ADOPTS it → a real dependent (R12); otherwise the export SYNTHESIZES it on
  // demand — not a purgeable current-document dependency.
  const recordName = builtinArrowBlockRecordName(value);
  if (recordName !== null) {
    if (ctx.definedBlockNames?.has(recordName.toLowerCase())) {
      return { kind: "block-name", name: recordName };
    }
    return { kind: "builtin-synthesized", name: recordName };
  }
  // (5) A custom name with no block and not a built-in arrow → dropped on export.
  return UNRESOLVED_RESOLUTION;
}

/**
 * Classify one MLEADER / MLEADERSTYLE arrowhead value — DISTINCT from the DIMENSION
 * DSTYLE path ({@link resolveArrowheadRef}). SL-11 round-6 (@codex): the DIMENSION
 * semantics (canonicalize an unprefixed built-in to its `_<NAME>` record + adopt it
 * / synthesize an absent built-in on export) do NOT apply to MLEADER arrowheads.
 *
 * The MLEADER entity `arrowheadBlock` (AcDbMLeader body group 342 = arrow_head_
 * handle) and leader-line `arrowheadBlock` are copied VERBATIM to the writer, and
 * the MLEADERSTYLE `arrowheadBlock` (341) is resolved NAME→handle and DROPPED when
 * the name has no live BLOCK_RECORD — in NO case is a built-in arrow auto-
 * materialized (`resolveMLeaderStyleReferenceHandles` / the entity body-342 write
 * never call `ARROWS.create_block`, unlike `stampDimensionDStyleArrowheadHandles`).
 * So the value denotes a REAL, live-document block ONLY when it LITERALLY matches:
 *
 *   - `default`      — closed-filled / null default (`"" / "." / "_CLOSEDFILLED" /
 *     "0"`). No dependency; audit-clean.
 *   - `block-handle` — the value is a live BLOCK_RECORD handle → the block it
 *     targets (an imported body-342 soft-pointer). A live dependent. Tried FIRST
 *     (SL-11 round-7 @codex F2): the MLEADER body-342 value is fundamentally a
 *     HANDLE, so a hex value that is BOTH a live block-record handle AND spells an
 *     existing block name resolves by its HANDLE target, not the same-spelled name.
 *   - `block-name`   — a declared block whose name EQUALS the value, reached ONLY
 *     when the value is NOT a known block-record handle (NO built-in `_<NAME>`
 *     canonicalization: `DOT` is a dep on `_DOT` ONLY if declared as `DOT`, never
 *     adopting a declared `_DOT`). A live dependent.
 *   - `unresolved`   — anything else (a built-in arrow name with no matching block,
 *     a custom name, a dangling handle): exported verbatim → dangles on reopen. NOT
 *     synthesized. The audit flags it; no dependency is recorded.
 *
 * There is no `builtin-synthesized` verdict here — that is the DIMENSION-only class.
 */
export function resolveMleaderArrowheadRef(
  value: string,
  ctx: ArrowheadResolveContext,
): ArrowheadRefResolution {
  if (isDefaultArrowheadValue(value)) return DEFAULT_RESOLUTION;
  const key = value.toLowerCase();
  // SL-11 round-7 (@codex F2) — the MLEADER body-342 value is FUNDAMENTALLY a
  // HANDLE: the parser stores group 342 (arrow_head_handle) VERBATIM into
  // `arrowheadBlock` (entityParser.ts `342` reader) and the writer copies it back
  // verbatim (writer.ts body-342 emit), so on reopen AutoCAD reads it as a
  // soft-pointer HANDLE. Try BLOCK_RECORD handle resolution FIRST so a hex value
  // like `A1` that is BOTH a live block-record handle AND spells an existing block
  // NAME is classified by its real (handle) target — the block the exporter's
  // verbatim 342 actually points at — never the same-spelled block NAME (the
  // `A1`-handle vs `A1`-named-block ambiguity, SL-11 round-3 #3).
  const handleName = ctx.blockRecordHandlesToName?.get(key);
  if (handleName !== undefined) {
    return { kind: "block-handle", handle: value, name: handleName };
  }
  // NOT a known handle → fall back to a declared block whose NAME equals the literal
  // value (a from-scratch MLEADER may author its arrowhead by block NAME). NO
  // canonical `_<NAME>` built-in adoption (MLEADER copies the value verbatim; it
  // never adopts `_DOT` for a `DOT`).
  if (ctx.definedBlockNames?.has(key)) return { kind: "block-name", name: value };
  // A built-in arrow with no real block / a custom name / a dangling handle →
  // exported verbatim → dangles. NOT synthesized (the DIMENSION-only behavior).
  return UNRESOLVED_RESOLUTION;
}
